/**
 * De plaatsregel onder een geheugenregel, in de taal van het scherm. Los punt Engelse
 * resten, 21 oktober 2026: het scherm toonde de Engelse zinnen die de backend voor
 * het model maakt ("for the whole organisation", "today at 14:05"). De backend stuurt
 * nu ook de kale delen (`plaats`, `moment`, zie `routers/memory.py`) en deze module
 * maakt er de zin van. Ontbreken de delen, bij een oudere backend, dan blijft de oude
 * tekst staan.
 *
 * PUUR: `t` en `taal` komen van de aanroeper.
 */

import { localeVan as lokaal } from "./datum";

/** "overal", "project Uganda", enzovoort, of de oude tekst. */
export function plaatsTekst(item, t) {
  const p = item?.plaats;
  if (!p) return item?.place || "";
  if (p.soort === "overal") return t("overal");
  if (p.soort === "organisatie") return t("voor de hele organisatie");
  if (p.soort === "project") {
    return p.naam == null ? t("een project dat niet meer bestaat") : t("project {naam}", { naam: p.naam });
  }
  if (p.soort === "werkruimte") {
    if (p.naam == null) return t("een werkruimte die niet meer bestaat");
    // Herstel Geheugen NL: de standaardnaam is geen gekozen naam en staat dus in de
    // taal van het scherm, niet als "werkruimte Untitled workspace".
    if (p.naamloos) return t("Naamloze werkruimte");
    return t("werkruimte {naam}", { naam: p.naam });
  }
  return item.place || "";
}


/** De naam van een weekdag, 1 is maandag (ISO), in de taal van het scherm. */
export function weekdagNaam(isoweekdag, taal) {
  // 1 januari 2024 was een maandag.
  return new Intl.DateTimeFormat(lokaal(taal), { weekday: "long", timeZone: "UTC" })
    .format(new Date(Date.UTC(2024, 0, isoweekdag)));
}

/** De naam van een maand, 1 is januari, in de taal van het scherm. */
export function maandNaam(maand, taal) {
  return new Intl.DateTimeFormat(lokaal(taal), { month: "long", timeZone: "UTC" })
    .format(new Date(Date.UTC(2024, maand - 1, 1)));
}

/** "vandaag om 14:05", "afgelopen dinsdag om 09:12", "12 maart", of de oude tekst. */
export function wanneerTekst(item, t, taal) {
  const m = item?.moment;
  if (!m) return item?.when || "";
  if (m.soort === "vandaag") return t("vandaag om {tijd}", { tijd: m.tijd });
  if (m.soort === "gisteren") return t("gisteren om {tijd}", { tijd: m.tijd });
  if (m.soort === "weekdag") {
    return t("afgelopen {dag} om {tijd}", { dag: weekdagNaam(m.weekdag, taal), tijd: m.tijd });
  }
  if (m.soort === "week") return t("een week geleden");
  if (m.soort === "datum") {
    const dag = `${m.dag} ${maandNaam(m.maand, taal)}`;
    return m.met_jaar ? `${dag} ${m.jaar}` : dag;
  }
  return item.when || "";
}

/**
 * Waar een herinnering vandaan komt, in de taal van het scherm. Herstel Geheugen NL,
 * 21 oktober 2026: de backend slaat de herkomst op als Engelse zin ("you approved
 * this on 19 September 2026") en stuurt nu ook de kale delen (`herkomst`, zie
 * `services/herkomst.py`). Zonder delen blijft de opgeslagen tekst staan: wat iemand
 * zelf als herkomst opgaf, wordt niet vertaald.
 */
export function herkomstTekst(item, t, taal) {
  const h = item?.herkomst;
  if (!h) return item?.source || "";
  if (h.soort === "gevraagd") return t("je vroeg me het te onthouden");
  if (h.soort === "gezegd") return t("iets wat je zei");
  if (h.soort === "goedgekeurd") {
    return t("je keurde dit goed op {datum}", { datum: `${h.dag} ${maandNaam(h.maand, taal)} ${h.jaar}` });
  }
  if (h.soort === "agentrun") return t("agentrun van {pad}", { pad: h.pad });
  if (h.soort === "run") return t("run van {pad}", { pad: h.pad });
  if (h.soort === "gesprek") return t("gesprek {id}", { id: h.id });
  return item.source || "";
}
